import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Renders a merchant-branded PDF receipt for a paid Nebula invoice and
 * triggers a browser download. Runs 100% client-side (no backend churn, no
 * PII leaves the merchant's device beyond what the merchant already sees).
 *
 * Layout:
 *   • Deep-space purple gradient header with Nebula 🛸 wordmark
 *   • Merchant block (name + email + business address if present)
 *   • Invoice block (invoice_id + issued/paid stamps + Stripe session id)
 *   • Line-item table (description + amount)
 *   • Fee breakdown (gross · Stripe fee · 1% platform fee · merchant net)
 *   • Statutory-reserve footer with the 4-step Cash Flash micro-summary
 *   • QR code footer of the short link so the customer can rescan for the
 *     original checkout page (via free api.qrserver.com — image link, no key)
 */
export async function renderNebulaReceiptPdf({ invoice, merchant }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // === Deep-space header band ===============================================
  doc.setFillColor(30, 15, 60);
  doc.rect(0, 0, W, 110, "F");
  // Merchant logo hot-swap — if a logo is uploaded, load and place it in the
  // header band instead of the default 🛸 circle wordmark. Uses async image
  // fetch via fetch → dataURL so jsPDF can embed it.
  let logoDrawn = false;
  if (merchant?.merchant_logo_ext && merchant?.user_id) {
    try {
      const base = (typeof window !== "undefined" && window.__RS_API_BASE) || "";
      const logoUrl = base
        ? `${base}/api/auth/logo/${merchant.user_id}.${merchant.merchant_logo_ext}`
        : `/api/auth/logo/${merchant.user_id}.${merchant.merchant_logo_ext}`;
      const dataUrl = await fetch(logoUrl).then((r) => r.blob()).then((b) => new Promise((res) => {
        const reader = new FileReader();
        reader.onloadend = () => res(reader.result);
        reader.readAsDataURL(b);
      }));
      const fmt = merchant.merchant_logo_ext.toUpperCase() === "JPG" ? "JPEG" : merchant.merchant_logo_ext.toUpperCase();
      if (fmt !== "SVG") {
        doc.addImage(dataUrl, fmt, 36, 26, 60, 60, undefined, "FAST");
        logoDrawn = true;
      }
    } catch { /* fall through to default wordmark */ }
  }
  if (!logoDrawn) {
    doc.setFillColor(139, 92, 246);
    doc.circle(56, 55, 22, "F");
    doc.setFillColor(236, 72, 153);
    doc.circle(72, 55, 12, "F");
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("Nebula Settlement Receipt", 108, 52);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(196, 181, 253);
  doc.text("Powered by Receipt Sheets · Premium Tax Shield", 108, 70);
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text(`Invoice · ${invoice.invoice_id}`, W - 200, 52);
  doc.text(
    `Issued ${new Date(invoice.created_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}`,
    W - 200,
    68,
  );
  if (invoice.paid_at) {
    doc.setTextColor(110, 231, 183);
    doc.text(
      `Paid ${new Date(invoice.paid_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}`,
      W - 200,
      84,
    );
  }

  // === Merchant block ======================================================
  doc.setTextColor(30, 41, 59);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("From", 40, 148);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text(merchant?.name || merchant?.email || "Receipt Sheets Merchant", 40, 164);
  if (merchant?.email) doc.text(merchant.email, 40, 178);
  if (merchant?.address) doc.text(merchant.address, 40, 192);

  // === Invoice block =======================================================
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(30, 41, 59);
  doc.text("Invoice For", W / 2 + 20, 148);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text("Client (via Nebula Payment Link)", W / 2 + 20, 164);
  if (invoice.stripe_session_id) {
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Stripe session · ${invoice.stripe_session_id.slice(0, 40)}`, W / 2 + 20, 180);
  }

  // === Line-item table =====================================================
  const gross = (invoice.amount_cents || 0) / 100;
  const stripeFee = (invoice.actual_stripe_fee_cents ?? invoice.estimated_stripe_fee_cents ?? 0) / 100;
  const platformFee = (invoice.application_fee_cents || 0) / 100;
  const net = (invoice.net_cents ?? invoice.estimated_net_cents ?? gross - stripeFee - platformFee) / 100;

  autoTable(doc, {
    startY: 220,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 8, textColor: [30, 41, 59] },
    headStyles: { fillColor: [30, 15, 60], textColor: [244, 244, 245], fontStyle: "bold" },
    columnStyles: { 1: { halign: "right" } },
    head: [["Description", "Amount"]],
    body: [[invoice.description || "Service", `$${gross.toFixed(2)}`]],
  });

  // === Fee waterfall =======================================================
  const waterfallY = (doc.lastAutoTable?.finalY || 260) + 24;
  autoTable(doc, {
    startY: waterfallY,
    theme: "plain",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 6, textColor: [51, 65, 85] },
    columnStyles: { 0: { cellWidth: 260 }, 1: { halign: "right", cellWidth: 100 } },
    body: [
      ["Gross invoice",                           `$${gross.toFixed(2)}`],
      ["Stripe processing fee (2.9% + $0.30)",    `−$${stripeFee.toFixed(2)}`],
      ["Nebula platform application fee (1.00%)", `−$${platformFee.toFixed(2)}`],
      [{ content: "Merchant net", styles: { fontStyle: "bold", textColor: [22, 163, 74] } },
       { content: `$${net.toFixed(2)}`, styles: { fontStyle: "bold", textColor: [22, 163, 74] } }],
    ],
  });

  // === Statutory reserve micro-summary ====================================
  const netCents = Math.round(net * 100);
  const base = net * 0.9235;
  const ss = Math.min(base, 184500) * 0.124;
  const med = base * 0.029;
  const sur = Math.max(0, base - 200000) * 0.009;
  const bufferCents = Math.round((ss + med + sur) * 100);
  const buffer = bufferCents / 100;
  const keep = (netCents - bufferCents) / 100;

  const reserveY = (doc.lastAutoTable?.finalY || 420) + 24;
  doc.setFillColor(240, 253, 244);
  doc.roundedRect(40, reserveY, W - 80, 78, 6, 6, "F");
  doc.setTextColor(22, 101, 52);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("🛡  4-Step Statutory Cash Flash Reserve · IRC § 1401", 52, reserveY + 20);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(30, 41, 59);
  doc.text(`Net cascaded to Cash Flash:     $${net.toFixed(2)}`, 52, reserveY + 38);
  doc.text(`Statutory tax buffer (SS+Med):  $${buffer.toFixed(2)}`, 52, reserveY + 52);
  doc.text(`Merchant retention (after buf): $${keep.toFixed(2)}`, 52, reserveY + 66);

  // === Footer + QR =========================================================
  const footerY = H - 100;
  if (invoice.short_link) {
    // free QR image service — no API key required
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=140x140&format=png&data=${encodeURIComponent(invoice.short_link)}`;
    try {
      const img = await fetch(qrUrl).then((r) => r.blob()).then((b) => new Promise((res) => {
        const reader = new FileReader();
        reader.onloadend = () => res(reader.result);
        reader.readAsDataURL(b);
      }));
      doc.addImage(img, "PNG", W - 130, footerY - 20, 90, 90);
    } catch { /* QR is a nice-to-have — skip on fetch error */ }
  }
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text("Powered by Receipt Sheets · Nebula Inbound Settlement Vault", 40, footerY + 40);
  doc.setFontSize(7);
  doc.setTextColor(148, 163, 184);
  doc.text(
    "This receipt is generated client-side and cryptographically anchored to your Stripe session id.",
    40,
    footerY + 54,
  );
  doc.text(
    "Refunds are issued from your Stripe Express dashboard. Platform application fee is non-refundable per Choice 1.",
    40,
    footerY + 66,
  );

  doc.save(`nebula-receipt-${invoice.invoice_id}.pdf`);
}
